import Toggle from 'components/ReusableComponents/Toggle/index'
import { hotelCampaignRegistrationStatuses } from 'main/hotelStatuses'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getHealthCheckAutoInviteData } from '../../../actions/healthCheck'
import CustomHeader from '../../../components/ReusableComponents/HeaderTitles'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import Table from '../../../components/ReusableComponents/Table'
import Tab from '../../../components/ReusableComponents/Tabs'
import HealthCheckFilters from '../Common/Filters'
import useHealthCheckFilters from '../Common/useHealthCheckFilters'
import styles from './HealthCheckAutoInvite.module.scss'
import AutoInviteHealthRow from './Row'
import { getSortedResults } from './sortHelper'
import AutoInviteHealthTitleRow from './TitleRow'
import TotalRow from './TotalRow'

const HealthCheckAutoInvite = () => {
  const dispatch = useDispatch()

  const isLoading = useSelector(s => s.healthCheck.invites.isLoading)
  const hotelData = useSelector(s => s.healthCheck.invites.hotelData)
  const allhotelGroups = useSelector(s => s.healthCheck.invites.hotelGroups)
  const allhotelChains = useSelector(s => s.healthCheck.invites.hotelChains)

  const [filterState, filterDispatch] = useHealthCheckFilters('auto-invite')
  const [sortDecreasing, setSortDecreasing] = useState(false)
  const [sortByIndex, setSortByIndex] = useState(3)
  const [results, setResults] = useState([])

  // Alert status filter toggles
  const [showAlert, setShowAlert] = useState(true)
  const [showWarning, setShowWarning] = useState(true)
  const [showGreen, setShowGreen] = useState(true)

  // Calculate row status for AutoInvite tab
  const calculateRowStatus = row => {
    const { invited, bookings, conversions, marketingCampaigns } = row
    const inviteCount = invited?.periodCurrent || 0
    const invitePrevious = invited?.periodPrevious || 0
    const bookingCount = bookings?.periodCurrent || 0
    const conversionCount = conversions?.periodCurrent || 0

    // Check for significant reduction in invites (> 30% drop)
    const hasSignificantReduction =
      invitePrevious > 0 &&
      inviteCount < invitePrevious &&
      (invitePrevious - inviteCount) / invitePrevious > 0.3

    // Calculate disabled campaigns percentage (3 Mo, 6 Mo, 9 Mo, 12 Mo, 15 Mo, 15 Mo+, After visit)
    const inviteStatusCampaignCategories = [
      'Lost3Months',
      'Lost6Months',
      'Lost9Months',
      'Lost12Months',
      'Lost15Months',
      'Lost15MonthsPlus',
      'AfterLastVisit'
    ]

    let disabledCount = 0
    let totalCampaigns = 0
    if (marketingCampaigns && marketingCampaigns.length > 0) {
      inviteStatusCampaignCategories.forEach(category => {
        const campaign = marketingCampaigns.find(c => c.category === category)
        if (campaign) {
          totalCampaigns++
          if (!campaign.isActive) {
            disabledCount++
          }
        }
      })
    }

    const disabledPercentage = totalCampaigns > 0 ? (disabledCount / totalCampaigns) * 100 : 0

    // ALERT conditions:
    // 1. No invites sent
    if (inviteCount === 0) {
      return 'alert'
    }

    // 2. Significant reduction in invites (> 30% drop)
    if (hasSignificantReduction) {
      return 'alert'
    }

    // 3. No bookings or conversions despite having invites
    if (inviteCount > 0 && bookingCount === 0 && conversionCount === 0) {
      return 'alert'
    }

    // 4. More than 50% of invite campaigns are disabled
    if (disabledPercentage > 50) {
      return 'alert'
    }

    // WARNING conditions:
    // 1. Low invites (< 10)
    if (inviteCount > 0 && inviteCount < 10) {
      return 'warning'
    }

    // 2. Low conversion rate (< 5% if invites > 10)
    if (inviteCount >= 10 && conversionCount > 0) {
      const conversionRate = conversionCount / inviteCount
      if (conversionRate < 0.05) {
        return 'warning'
      }
    }

    // 3. 50-80% of invite campaigns are disabled
    if (disabledPercentage >= 50 && disabledPercentage <= 80) {
      return 'warning'
    }

    // GREEN conditions:
    // Good invite count and reasonable conversion rate, and < 50% disabled
    if (disabledPercentage < 50) {
      if (inviteCount >= 10 && conversionCount > 0) {
        const conversionRate = conversionCount / inviteCount
        if (conversionRate >= 0.05) {
          return 'green'
        }
      }
      // If < 50% disabled, still green even if conversion rate is lower
      if (inviteCount >= 10) {
        return 'green'
      }
    }

    // Default to warning if status is unclear
    return 'warning'
  }

  // Generate explanation for row status
  const getStatusExplanation = row => {
    const { invited, bookings, conversions, marketingCampaigns } = row
    const inviteCount = invited?.periodCurrent || 0
    const invitePrevious = invited?.periodPrevious || 0
    const bookingCount = bookings?.periodCurrent || 0
    const conversionCount = conversions?.periodCurrent || 0

    const hasSignificantReduction =
      invitePrevious > 0 &&
      inviteCount < invitePrevious &&
      (invitePrevious - inviteCount) / invitePrevious > 0.3

    const inviteStatusCampaignCategories = [
      'Lost3Months',
      'Lost6Months',
      'Lost9Months',
      'Lost12Months',
      'Lost15Months',
      'Lost15MonthsPlus',
      'AfterLastVisit'
    ]

    let disabledCount = 0
    let totalCampaigns = 0
    if (marketingCampaigns && marketingCampaigns.length > 0) {
      inviteStatusCampaignCategories.forEach(category => {
        const campaign = marketingCampaigns.find(c => c.category === category)
        if (campaign) {
          totalCampaigns++
          if (!campaign.isActive) {
            disabledCount++
          }
        }
      })
    }

    const disabledPercentage = totalCampaigns > 0 ? (disabledCount / totalCampaigns) * 100 : 0
    const status = calculateRowStatus(row)
    const reasons = []

    if (status === 'alert') {
      if (inviteCount === 0) {
        reasons.push('No invites sent')
      }
      if (hasSignificantReduction) {
        const reductionPercent = ((invitePrevious - inviteCount) / invitePrevious * 100).toFixed(1)
        reasons.push(`Significant reduction in invites (${reductionPercent}% drop)`)
      }
      if (inviteCount > 0 && bookingCount === 0 && conversionCount === 0) {
        reasons.push('No bookings or conversions despite having invites')
      }
      if (disabledPercentage > 50) {
        reasons.push(`Features enabled: ${(100 - disabledPercentage).toFixed(0)}% (${disabledCount}/${totalCampaigns} disabled, > 50% disabled)`)
      }
    } else if (status === 'warning') {
      if (inviteCount > 0 && inviteCount < 10) {
        reasons.push(`Low invites: ${inviteCount} (< 10)`)
      }
      if (inviteCount >= 10 && conversionCount > 0) {
        const conversionRate = (conversionCount / inviteCount * 100).toFixed(1)
        if (conversionRate < 5) {
          reasons.push(`Low conversion rate: ${conversionRate}% (< 5%)`)
        }
      }
      if (disabledPercentage >= 50 && disabledPercentage <= 80) {
        reasons.push(`Features enabled: ${(100 - disabledPercentage).toFixed(0)}% (${disabledCount}/${totalCampaigns} disabled, 50-80% disabled)`)
      }
    } else if (status === 'green') {
      if (disabledPercentage < 50) {
        reasons.push(`Features enabled: ${(100 - disabledPercentage).toFixed(0)}% (${totalCampaigns - disabledCount}/${totalCampaigns} enabled, < 50% disabled)`)
      }
      if (inviteCount >= 10) {
        reasons.push(`Invites: ${inviteCount}`)
      }
      if (conversionCount > 0) {
        const conversionRate = (conversionCount / inviteCount * 100).toFixed(1)
        reasons.push(`Conversion rate: ${conversionRate}%`)
      }
    }

    return reasons.length > 0 ? reasons.join(' • ') : 'Status: ' + status
  }

  const widths = filterState.showTelcoDetails
    ? [40, 264, 75, 75, 75, 75, 75, 75, 75, 75, 75, 75, 75, 350]
    : [40, 350, 80, 80, 80, 80, 82, 60, 60, 60, 60, 60, 60, 60, 60, 60, 350]

  useEffect(() => {
    dispatch(getHealthCheckAutoInviteData(filterState.periodStart, filterState.periodEnd))
  }, [dispatch, filterState.periodEnd, filterState.periodStart])

  useEffect(() => {
    function getFilteredResults(hotelData) {
      const {
        searchString,
        chains,
        brands,
        hotelGroups,
        subsidiaryStatus,
        csmIds,
        showLowOutreaches,
        showLowConversions
      } = filterState

      const filtered = hotelData
        .filter(h => h.searchKey.match(new RegExp(searchString, 'i')))
        .filter(h => (hotelGroups === 'all' ? true : h.groupName === hotelGroups))
        .filter(h => (chains === 'all' ? true : h.chainCode === chains))
        .filter(h => (brands === 'all' ? true : h.parentChainCode === brands))
        .filter(h => (showLowOutreaches === true ? h.bookings.periodCurrent === 0 : true))
        .filter(h =>
          showLowConversions === true ? h.conversions.periodCurrent === 0 : true
        )
        .filter(h => {
          if (subsidiaryStatus.length === 0) {
            return true
          } else if (hotelCampaignRegistrationStatuses.includes(h.subsidiaryStatus)) {
            return subsidiaryStatus.includes('PendingCampaignBrandRegistration')
          } else {
            return subsidiaryStatus.includes(h.subsidiaryStatus)
          }
        })
        .filter(h => {
          if (csmIds.length === 0) {
            return true
          } else {
            return csmIds.includes(`${h.csmUserId}`)
          }
        })

      // Apply status filter
      return filtered.filter(h => {
        const status = calculateRowStatus(h)
        if (status === 'alert') return showAlert
        if (status === 'warning') return showWarning
        if (status === 'green') return showGreen
        return true
      })
    }

    function getResults() {
      const results = getSortedResults(
        getFilteredResults(hotelData),
        filterState,
        sortByIndex,
        sortDecreasing
      )
      setResults(results)
    }

    getResults()
  }, [filterState, hotelData, sortByIndex, sortDecreasing, showAlert, showWarning, showGreen])

  function handleTitleCellClick(rowIndex) {
    if (sortByIndex === rowIndex) {
      setSortDecreasing(d => !d)
    }
    setSortByIndex(rowIndex)
  }

  return (
    <div className={styles.wrapper}>
      <CustomHeader title="Hotel health check" width={1450} isFixed />
      <Tab.Parent>
        <Tab to="/healthCheck/pms">PMS</Tab>
        <Tab to="/healthCheck/autoinvite">AutoInvite</Tab>
        <Tab to="/healthCheck/billing">Billing</Tab>
        <Tab to="/healthCheck/performance">Performance</Tab>
        <Tab to="/healthCheck/summary">Summary</Tab>
      </Tab.Parent>
      <HealthCheckFilters
        state={filterState}
        dispatch={filterDispatch}
        allhotelGroups={allhotelGroups}
        allhotelChains={allhotelChains}
        showTelcoToggle
        showLowOutreachToggle
        showLowConversionsToggle
        showDateFilter
        showSubsidiaryStatus
        showCsmFilter
      >
        <div className={styles.alertStatusControls}>
          <span className={styles.alertStatusControlsTitle}>Filter by Status:</span>
          <Toggle
            className={styles.toggle}
            checked={showAlert}
            handleChange={e => setShowAlert(e.target.checked)}
            label="Alert"
          />
          <Toggle
            className={styles.toggle}
            checked={showWarning}
            handleChange={e => setShowWarning(e.target.checked)}
            label="Warning"
          />
          <Toggle
            className={styles.toggle}
            checked={showGreen}
            handleChange={e => setShowGreen(e.target.checked)}
            label="Green"
          />
        </div>
      </HealthCheckFilters>
      <div className={styles.tableContainer}>
        {isLoading ? (
          <LoadingIndicator marginTop={100} />
        ) : (
          <Table
            widths={widths}
            handleTitleCellClick={handleTitleCellClick}
            sortIndex={sortByIndex}
            sortDirection={sortDecreasing ? 'down' : 'up'}
          >
            <AutoInviteHealthTitleRow
              showTelcoDetails={filterState.showTelcoDetails}
              sortByIndex={sortByIndex}
              sortDecreasing={sortDecreasing}
            />

            {results.length === 0 && (
              <div className={styles.noResults}>
                No matching results. Try modifiying the filters.
              </div>
            )}
            {results.map((h, i) => {
              const rowStatus = calculateRowStatus(h)
              const statusExplanation = getStatusExplanation(h)
              return (
                <AutoInviteHealthRow
                  {...h}
                  index={i + 1}
                  showTelcoDetails={filterState.showTelcoDetails}
                  rowStatus={rowStatus}
                  statusExplanation={statusExplanation}
                />
              )
            })}
            <TotalRow showTelcoDetails={filterState.showTelcoDetails} data={results} />
          </Table>
        )}
      </div>
    </div>
  )
}

export default HealthCheckAutoInvite
